import {
  Box,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import {
  getDocumentInfoForPublication,
  PublicationContactInfo,
  PublicationDocumentInfo,
  PublicationStakeholderInfo,
  publishInvoiceDebt,
} from 'lib/api/documentPublication';
import CustomButton from 'lib/designSystem/Button';
import { applyDefaults, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { FormProps } from '../..';

const emptyDocument: PublicationDocumentInfo & { paymentDate: string } = {
  id: '',
  businessIdentifier: '',
  folio: '',
  amount: 0,
  type: '',
  subType: '',
  issuedDate: '',
  paymentDate: '',
};

const emptyStakeholderInfo = {
  name: '',
  identifier: '',
};

const emptyContanctInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
};

const InvoiceForm: React.FC<FormProps> = ({
  documentId,
  setLoading,
  setOpen,
  handleSuccessClose,
  setIdentifier,
}: FormProps) => {
  const dispatch = useDispatch();
  const [publicationDocumentInfo, setPublicationDocumentInfo] = useState<
    PublicationDocumentInfo & { paymentDate: string }
  >(emptyDocument);
  const [stakeholderForm, setstakeholderForm] =
    useState<PublicationStakeholderInfo>(emptyStakeholderInfo);
  const [contactForm, setContactForm] =
    useState<PublicationContactInfo>(emptyContanctInfo);

  const handleDocumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPublicationDocumentInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectDocumentChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setPublicationDocumentInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStakeholderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setstakeholderForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const generateForm = (
    document: PublicationDocumentInfo,
    paymentDate: string,
  ) => {
    const newDocument = { ...document, paymentDate };
    newDocument.folio = newDocument.folio.split('-')[0];
    newDocument.issuedDate = newDocument.issuedDate.substring(0, 10);
    newDocument.paymentDate = newDocument.paymentDate.substring(0, 10);
    return applyDefaults(newDocument, emptyDocument);
  };

  const getDocumentInfo = async () => {
    const response = await getDocumentInfoForPublication(dispatch, documentId!);
    if (!response) return;
    const { document, stakeholder, contactInfo, paymentDate } = response;
    setPublicationDocumentInfo(generateForm(document, paymentDate ?? ''));
    setstakeholderForm({ ...emptyStakeholderInfo, ...stakeholder });
    setContactForm({ ...emptyContanctInfo, ...contactInfo });
  };

  const handlePublish = async () => {
    try {
      setLoading(true);
      const payload = {
        document: publicationDocumentInfo,
        stakeholder: stakeholderForm,
        contact: contactForm,
      };
      await publishInvoiceDebt(dispatch, payload);
      setLoading(false);
      setIdentifier(stakeholderForm.identifier);
      handleSuccessClose();
    } catch (error) {
      setLoading(false);
      showAlert(
        dispatch,
        'error',
        'Hubo un problema al publicar el documento, revisa que los campos sean correctos',
      );
    }
  };

  useEffect(() => {
    if (!documentId) return;
    try {
      setLoading(true);
      void getDocumentInfo();
      setLoading(false);
    } catch (error) {
      console.error(error);
    }
  }, [documentId]);

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel id="filter-label">Código de factura</InputLabel>
            <Select
              labelId="filter-label"
              id="Filtrar por estado"
              fullWidth
              name="subType"
              value={publicationDocumentInfo.subType}
              onChange={handleSelectDocumentChange}
              label="Código de factura"
            >
              {['33', '34'].map((x: string, index: number) => (
                <MenuItem key={index} value={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Folio"
            variant="outlined"
            required
            name="folio"
            value={publicationDocumentInfo.folio}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="RUT emisor"
            variant="outlined"
            required
            name="businessIdentifier"
            value={publicationDocumentInfo.businessIdentifier}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="RUT deudor"
            variant="outlined"
            required
            name="identifier"
            value={stakeholderForm.identifier}
            onChange={handleStakeholderChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Razón social deudor"
            variant="outlined"
            required
            name="name"
            value={stakeholderForm.name}
            onChange={handleStakeholderChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Monto"
            variant="outlined"
            required
            name="amount"
            value={publicationDocumentInfo.amount}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Fecha emisión documento"
            type="date"
            name="issuedDate"
            value={publicationDocumentInfo.issuedDate}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Fecha vencimiento documento"
            type="date"
            name="paymentDate"
            value={publicationDocumentInfo.paymentDate}
            onChange={handleDocumentChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Nombre contacto deudor"
            variant="outlined"
            required
            name="firstName"
            value={contactForm.firstName}
            onChange={handleContactChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Apellido contacto deudor"
            variant="outlined"
            required
            name="lastName"
            value={contactForm.lastName}
            onChange={handleContactChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Teléfono contacto deudor"
            variant="outlined"
            required
            name="phone"
            value={contactForm.phone}
            onChange={handleContactChange}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Correo contacto deudor"
            variant="outlined"
            required
            name="email"
            value={contactForm.email}
            onChange={handleContactChange}
          />
        </Grid>
      </Grid>
      <Box sx={{ my: 3 }} />
      <DialogActions>
        <CustomButton color="secondary" onClick={() => setOpen(false)}>
          Cancelar
        </CustomButton>
        <CustomButton color="primary" onClick={() => handlePublish()}>
          Publicar
        </CustomButton>
      </DialogActions>
    </div>
  );
};

export default InvoiceForm;
